import { Box, CheckBox, Flex, Grid, Text } from 'components'
import { startCase } from 'lodash-es'
import { Fragment, useEffect, useState } from 'react'
import { APIScope, APIScopeMethod, APIScopeResource, IExternalClient } from 'types'

interface ScopeOption {
  id: string
  label: string
}

interface Props {
  onChange: (body: Partial<IExternalClient>) => void
  scopes?: APIScope[]
}

export default function ScopeEditor({ onChange, scopes }: Props) {
  const [scopeValues, setScopeValues] = useState<Record<string, Record<string, boolean>>>(() =>
    initializeScopeValues(scopes)
  )

  function handleCheckboxChange(rowId: string, colId: string, checked: boolean | 'indeterminate') {
    if (checked === 'indeterminate') return
    setScopeValues(prev => {
      const newValues = { ...prev }
      newValues[rowId] = { ...newValues[rowId], [colId]: checked }
      return newValues
    })
  }

  function handleAllCheckboxChange(rowId: string, checked: boolean | 'indeterminate') {
    if (checked === 'indeterminate') return
    setScopeValues(prev => {
      const newValues = { ...prev }
      newValues[rowId] = {}
      defaultColumnHeaders.forEach(col => {
        newValues[rowId][col] = checked
      })
      return newValues
    })
  }

  function getAllCheckboxState(rowId: string): boolean | 'indeterminate' {
    const checkedCount = defaultColumnHeaders.filter(col => scopeValues[rowId]?.[col]).length
    if (checkedCount === 0) return false
    if (checkedCount === defaultColumnHeaders.length) return true
    return 'indeterminate'
  }

  function convertToScopes(values: Record<string, Record<string, boolean>>): APIScope[] {
    const scopes: APIScope[] = []
    Object.entries(values).forEach(([rowId, rowValues]) => {
      Object.entries(rowValues).forEach(([colId, checked]) => {
        if (checked) {
          scopes.push(`${colId}_${rowId}` as APIScope)
        }
      })
    })
    return scopes
  }

  useEffect(() => {
    const scopes = convertToScopes(scopeValues)
    onChange({ scopes })
  }, [scopeValues])

  return (
    <Flex direction="column" gap={2}>
      <Flex direction="column" gap={1} opacity={0.7}>
        <Text textStyle="bodyMedium">Edit Scope</Text>
        <Text textStyle="bodyXSmall" fontWeight="normal">
          Select which APIs you want to grant access to.
        </Text>
      </Flex>
      <Grid templateColumns={`repeat(${defaultColumnHeaders.length + 2}, 1fr)`} gap={2}>
        <Box key="filler" />
        {defaultColumnHeaders.map(col => (
          <Text key={col} {...headerTextProps}>
            {startCase(col)}
          </Text>
        ))}
        <Text key="all" {...headerTextProps}>
          All
        </Text>

        {defaultRowHeaders.map(row => (
          <Fragment key={row}>
            <Text key={`${row}-label`} textStyle="bodySmall">
              {row === 'workspaces' ? 'Workspace' : startCase(row)}
            </Text>
            {defaultColumnHeaders.map(col => (
              <Flex key={`${row}-${col}`} justify="center">
                <CheckBox
                  checked={scopeValues[row]?.[col] || false}
                  onChange={checked => handleCheckboxChange(row, col, checked)}
                />
              </Flex>
            ))}
            <Flex key={`${row}-all`} justify="center">
              <CheckBox
                checked={getAllCheckboxState(row)}
                onChange={checked => handleAllCheckboxChange(row, checked)}
              />
            </Flex>
          </Fragment>
        ))}
      </Grid>
    </Flex>
  )
}

const defaultRowHeaders: APIScopeResource[] = [
  'events',
  'facilities',
  'leads',
  'sessions',
  'users',
  'workspaces',
]
const defaultColumnHeaders: APIScopeMethod[] = ['read', 'create', 'update', 'remove']

function initializeScopeValues(existingScopes?: APIScope[]) {
  const initialValues: Record<string, Record<string, boolean>> = {}
  defaultRowHeaders.forEach(row => {
    initialValues[row] = {}
    defaultColumnHeaders.forEach(col => {
      initialValues[row][col] = false
    })
  })
  if (existingScopes) {
    existingScopes.forEach(scope => {
      const [colId, rowId] = scope.split('_')
      if (initialValues[rowId] && colId in initialValues[rowId]) {
        initialValues[rowId][colId] = true
      }
    })
  }
  return initialValues
}

const headerTextProps = {
  textAlign: 'center',
  textStyle: 'bodySmall',
}
